import { useState, useCallback, useEffect, useMemo, memo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Typography, makeStyles } from '@material-ui/core';
import PageContainer from '../../shared/pageContainer';
import PermissionsMatrix from '../../shared/permissionsMatrix';
import CustomButton from '../../shared/customButton';
import CustomFormDrawer from '../../shared/customFormDrawer';
import { APP_PAGES } from '../../../constants/pages';
import { ORG_SETTINGS_NAVIGATION_ITEMS } from '../../../constants/organizationSettings';
import {
  PERMISSION_ACTIONS,
  PERMISSION_CODES,
  REVIEW_GROUP,
  REVIEW_ROOT_VIEW_PERMISSION_CODES,
} from '../../../constants/permissions';
import {
  API_RBAC_ADDITIONAL_ROLES,
  api_rbac_role_permissions,
} from '../../../constants/apiRoutes';
import { rolesSelector } from '../../../store/selectors/rolesSelector';
import { permissionsSelector } from '../../../store/selectors/permissionsSelector';
import { getRoles, clearRoles } from '../../../store/modules/roles';
import {
  getPermissions,
  getPermissionsByRole,
  clearPermissions,
} from '../../../store/modules/permissions';
import { useTranslations } from '../../../utility/useTranslations';
import http from '../../../utility/http';
import { prepareAccessMatrixRoles } from '../../../utility/roles';
import {
  hasAuthPermissionCheck,
  preparePermissionsGroups,
  getNestedViewPairByViewCode,
} from '../../../utility/permissions';
import {
  isArrayEmpty,
  getItemIndex,
  replaceObjectInList,
  removeObjectFromArray,
  normalizeListOrder,
} from '../../../utility/helpers';
import {
  ADDITIONAL_ROLES_FIELD,
  prepareAdditionalRolesInitialData,
} from './config';

const useStyles = makeStyles(({ spacing }) => ({
  pageContainerMain: {
    display: 'flex',
    flexDirection: 'column',
  },
  pageContainerContent: {
    flex: 1,
    minHeight: 0,
  },
  root: {
    height: '100%',
  },
  descriptionWrapper: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: spacing(4),
  },
  manageAdditionalRolesButton: {
    flexShrink: 0,
  },
  pageDescription: {
    flex: 1,
    minWidth: 0,
    marginBottom: spacing(6),
  },
}));

const OrganizationSettingsPermisionsPage = ({
  accessControl,
  navigate,
  dispatch,
  organizationSettings,
  ...rest
}) => {
  const classes = useStyles();
  const translations = useTranslations(
    APP_PAGES.ORGANIZATION_SETTINGS_PERMISSIONS
  );

  const roles = useSelector(rolesSelector);
  const { permissionsGroups, permissionsByRole } =
    useSelector(permissionsSelector);

  const [parentNode, setParentNode] = useState(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isManageAdditionalRolesOpened, setIsManageAdditionalRolesOpened] =
    useState(false);

  const canAccessFeatures = hasAuthPermissionCheck(
    PERMISSION_CODES.SETTINGS_FEATURES_MANAGE,
    accessControl
  );

  const canAccessDocuments = hasAuthPermissionCheck(
    PERMISSION_CODES.SETTINGS_DOCUMENTS_MANAGE,
    accessControl
  );

  const canAccessProfileInfo = hasAuthPermissionCheck(
    PERMISSION_CODES.SETTINGS_PERSON_FIELDS_INFO_MANAGE,
    accessControl
  );

  const navigationItems = useMemo(() => {
    return ORG_SETTINGS_NAVIGATION_ITEMS.reduce((acc, navItem) => {
      if (
        (!canAccessFeatures && navItem.translationKey === 'features') ||
        (!canAccessProfileInfo && navItem.translationKey === 'profile-info') ||
        (!canAccessDocuments && navItem.translationKey === 'documents')
      ) {
        return acc;
      }
      return [
        ...acc,
        {
          ...navItem,
          name: translations.navigationItems[navItem.translationKey],
        },
      ];
    }, []);
  }, [
    translations.navigationItems,
    canAccessFeatures,
    canAccessProfileInfo,
    canAccessDocuments,
  ]);

  const allRoles = useMemo(
    () => prepareAccessMatrixRoles(translations, roles),
    [translations, roles]
  );

  const initialAdditionalRoles = useMemo(
    () => prepareAdditionalRolesInitialData(roles),
    [roles]
  );

  const permissionsGroupsTranslated = useMemo(
    () =>
      preparePermissionsGroups(
        translations.permissionsGroups,
        permissionsGroups
      ),
    [translations.permissionsGroups, permissionsGroups]
  );

  const getInitialData = async () => {
    try {
      await Promise.all([
        getRoles(dispatch),
        getPermissions(dispatch),
        getPermissionsByRole(dispatch),
      ]);
    } finally {
      setIsInitialLoad(false);
    }
  };

  const cleanup = useCallback(() => {
    dispatch(clearRoles());
    dispatch(clearPermissions());
  }, [dispatch]);

  const onOpenManageAdditionalRoles = useCallback(
    () => setIsManageAdditionalRolesOpened(true),
    []
  );

  const onCloseManageAdditionalRoles = useCallback(
    () => setIsManageAdditionalRolesOpened(false),
    []
  );

  const onTogglePermission =
    (
      role,
      permissionCode,
      permission,
      scope,
      group,
      action,
      isScopeSettingsExpanded = false
    ) =>
    async isChecked => {
      let permissions = [...permissionsByRole[role.name].permissions];
      const permissionIndex = getItemIndex(permissions, permissionCode, 'code');

      if (role.hasPermissionsWithMultipleScopes && isScopeSettingsExpanded) {
        if (isChecked) {
          if (permission) {
            const updatedPermission = {
              ...permission,
              scopes: [...permission.scopes, ...scope],
            };
            permissions = replaceObjectInList(
              permissions,
              permissionIndex,
              updatedPermission
            );
          } else {
            permissions.push({ code: permissionCode, scopes: scope });
          }
        } else {
          const updatedPermission = {
            ...permission,
            scopes: permission.scopes.filter(
              currentScope => currentScope !== scope[0]
            ),
          };
          const nestedViewPair = getNestedViewPairByViewCode(permissionCode);

          if (nestedViewPair) {
            permissions = permissions.reduce((acc, perm) => {
              if (perm.code.startsWith(nestedViewPair.prefix)) {
                const scopes = perm.scopes.filter(
                  currentScope => currentScope !== scope[0]
                );

                if (!isArrayEmpty(scopes))
                  return [
                    ...acc,
                    {
                      ...perm,
                      scopes,
                    },
                  ];

                return acc;
              }

              return [...acc, perm];
            }, []);
          } else if (action === PERMISSION_ACTIONS.VIEW) {
            permissions = permissions.reduce((acc, perm) => {
              if (perm.code.includes(group)) {
                const scopes = perm.scopes.filter(
                  currentScope => currentScope !== scope[0]
                );

                if (!isArrayEmpty(scopes))
                  return [
                    ...acc,
                    {
                      ...perm,
                      scopes,
                    },
                  ];

                return acc;
              }

              return [...acc, { ...perm }];
            }, []);
          } else if (!isArrayEmpty(updatedPermission.scopes)) {
            permissions = replaceObjectInList(
              permissions,
              permissionIndex,
              updatedPermission
            );
          } else {
            permissions = removeObjectFromArray(
              permissions,
              updatedPermission,
              'code'
            );
          }
        }

        await http.put(api_rbac_role_permissions(role.id), { permissions });
        return getPermissionsByRole(dispatch);
      }

      const nestedViewPair = getNestedViewPairByViewCode(permissionCode);

      if (isChecked) {
        permissions.push({
          code: permissionCode,
          scopes: scope,
        });
      } else if (nestedViewPair) {
        permissions = permissions.filter(
          perm => !perm.code.startsWith(nestedViewPair.prefix)
        );
      } else if (group === REVIEW_GROUP) {
        if (REVIEW_ROOT_VIEW_PERMISSION_CODES.includes(permissionCode)) {
          const otherRootViewPermissionCode =
            REVIEW_ROOT_VIEW_PERMISSION_CODES.find(c => c !== permissionCode);
          const isOtherRootViewPermissionChecked = permissions.find(
            p => p.code === otherRootViewPermissionCode
          );

          if (isOtherRootViewPermissionChecked) {
            permissions = removeObjectFromArray(
              permissions,
              permission,
              'code'
            );
          } else {
            const reviewsGroupCodes =
              permissionsGroupsTranslated
                .find(g => g.group === REVIEW_GROUP)
                ?.permissions.map(p => p.code) ?? [];
            permissions = permissions.filter(
              p => !reviewsGroupCodes.includes(p.code)
            );
          }
        } else if (action === PERMISSION_ACTIONS.VIEW) {
          const prefix = permissionCode.split('.')[0];
          permissions = permissions.filter(
            p => !p.code.startsWith(`${prefix}.`)
          );
        } else {
          permissions = removeObjectFromArray(permissions, permission, 'code');
        }
      } else if (action === PERMISSION_ACTIONS.VIEW) {
        permissions = permissions.filter(perm => !perm.code.includes(group));
      } else {
        permissions = removeObjectFromArray(permissions, permission, 'code');
      }

      await http.put(api_rbac_role_permissions(role.id), { permissions });
      getPermissionsByRole(dispatch);
    };

  const onSaveAdditionalRoles = useCallback(
    async ({ additionalRoles = [] }) => {
      const preparedRoles = {
        roles: normalizeListOrder(
          additionalRoles.filter(role => !role.shouldDelete)
        ),
      };

      await http.post(API_RBAC_ADDITIONAL_ROLES, preparedRoles);
      return Promise.all([getRoles(dispatch), getPermissionsByRole(dispatch)]);
    },
    [dispatch]
  );

  useEffect(() => {
    getInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  return (
    <PageContainer
      {...rest}
      mainClass={classes.pageContainerMain}
      contentClass={classes.pageContainerContent}
      translations={translations}
      accessControl={accessControl}
      organizationSettings={organizationSettings}
      navigate={navigate}
      navigationItems={navigationItems}
      shouldPassProps={false}
      isFullWidthContent
    >
      {!isInitialLoad && (
        <div ref={setParentNode} className={classes.root}>
          <div className={classes.descriptionWrapper}>
            <div className={classes.pageDescription}>
              <Typography variant="body2">
                {translations.descriptionPartOne}
              </Typography>
              <Typography variant="body2">
                {translations.descriptionPartTwo}
              </Typography>
            </div>
            <CustomButton
              className={classes.manageAdditionalRolesButton}
              type="addDarkRoundedOutlined"
              onClick={onOpenManageAdditionalRoles}
            >
              {translations.manageAdditionalRoles}
            </CustomButton>
          </div>
          <PermissionsMatrix
            parentNode={parentNode}
            translations={translations.permissionMatrix}
            roles={allRoles}
            permissionGroups={permissionsGroupsTranslated}
            permissionsByRole={permissionsByRole}
            onTogglePermission={onTogglePermission}
          />
          <CustomFormDrawer
            titleText={translations.manageAdditionalRolesForm.title}
            translations={translations.manageAdditionalRolesForm}
            isOpened={isManageAdditionalRolesOpened}
            initialData={initialAdditionalRoles}
            isInitialValid={
              !isArrayEmpty(initialAdditionalRoles?.additionalRoles)
            }
            fields={ADDITIONAL_ROLES_FIELD}
            onClose={onCloseManageAdditionalRoles}
            onSave={onSaveAdditionalRoles}
            hasCancelButton
            hideDelete
          />
        </div>
      )}
    </PageContainer>
  );
};

OrganizationSettingsPermisionsPage.propTypes = {
  accessControl: PropTypes.object.isRequired,
  organizationSettings: PropTypes.object.isRequired,
  navigate: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
};

export default memo(OrganizationSettingsPermisionsPage);
